// Entitlements TypeScript types for Zight frontend.
//
// These types map to the backend EntitlementService response structure.
// Capabilities are boolean flags, limits are numeric values (nil = unlimited),
// and quotas track usage with used/remaining values.

// Capability keys from PlanFeaturesAdapter, RoleAdapter, FeatureFlagAdapter, AddonAdapter
export interface EntitlementCapabilities {
  sso?: boolean;
  domainLockdown?: boolean;
  csvMemberImport?: boolean;
  customDomain?: boolean;
  securityFeatures?: boolean;
  customStorage?: boolean;
  collectLogs?: boolean;
  adminReports?: boolean;
  adminLastActivity?: boolean;
  aiSummary?: boolean;
  customBranding?: boolean;
  teamManagement?: boolean;
  teamAnalytics?: boolean;
  insights?: boolean;
  customDataRetention?: boolean;
  premiumIntegrations?: boolean;
  search?: boolean;
  anonymousComments?: boolean;
  premiumFileTypes?: boolean;
  callToAction?: boolean;
  invitations?: boolean;
  requestContent?: boolean;
  versioning?: boolean;
  viewerTranscription?: boolean;
  transcriptionEditing?: boolean;
  mergeVideos?: boolean;
  ocr?: boolean;
  // Dynamic capabilities from feature flags or addons
  [key: string]: boolean | undefined;
}

// Limit keys from PlanLimitsAdapter and FeatureFlagAdapter
// null/undefined = unlimited
export interface EntitlementLimits {
  maxVideoLength?: number | null;
  gifLength?: number | null;
  storage?: number | null;
  maxUploadSize?: number | null;
  bandwidth?: number | null;
  minBilledSeats?: number | null;
  monthlyUploads?: number | null;
  dailyUploads?: number | null;
  viewableItemsLimit?: number | null;
  smartActions?: number | null;
  contentLinkReplies?: number | null;
  recordingQuality?: number | null;
  maxMembers?: number | null;
  dropsBeforeSignup?: number | null;
  // Dynamic limits from feature flags
  [key: string]: number | null | undefined;
}

// Quota values with usage tracking
// null = not tracked or unlimited
export interface QuotaValue {
  used: number | null;
  remaining: number | null;
  expiresAt?: string | null;
}

// Quota keys from UsageAdapter
export interface EntitlementQuotas {
  // Organization-level quotas
  bandwidth?: QuotaValue;
  contentLinkReplies?: QuotaValue;
  // User-level quotas
  viewableItems?: QuotaValue;
  monthlyUploads?: QuotaValue;
  dailyUploads?: QuotaValue;
  smartActions?: QuotaValue;
  // Dynamic quotas
  [key: string]: QuotaValue | undefined;
}

// JSON:API resource format for each entitlement section
export interface EntitlementResource<T> {
  id: string | null;
  type: string;
  attributes: T;
}

// Full entitlements response from API
export interface EntitlementsResponse {
  meta: {
    version: number;
  };
  data: {
    capabilities: EntitlementResource<EntitlementCapabilities>;
    limits: EntitlementResource<EntitlementLimits>;
    quotas: EntitlementResource<EntitlementQuotas>;
  };
}

// Flattened entitlements for easier access in stores/components
export interface Entitlements {
  capabilities: EntitlementCapabilities;
  limits: EntitlementLimits;
  quotas: EntitlementQuotas;
}

// Typed feature configs pairing a limit key with its quota key.
// Pass these to entitlements store getters instead of raw strings.
export interface QuotaFeatureConfig {
  limitKey: keyof EntitlementLimits;
  quotaKey: keyof EntitlementQuotas;
}

export const QuotaFeature = {
  SMART_ACTIONS: { limitKey: 'smartActions', quotaKey: 'smartActions' },
  CONTENT_LINK_REPLIES: { limitKey: 'contentLinkReplies', quotaKey: 'contentLinkReplies' },
  VIEWABLE_ITEMS: { limitKey: 'viewableItemsLimit', quotaKey: 'viewableItems' },
} as const satisfies Record<string, QuotaFeatureConfig>;
